import { useSyncExternalStore } from "react";

/**
 * Floating chat windows (LinkedIn-style): chats docked at the bottom edge,
 * usable from any page. A tiny store outside React so anything can open one —
 * the header inbox, a toast popup, a chat room — and it survives navigation.
 */

export interface ChatWindowState {
  conversationId: number;
  minimized: boolean;
}

/** At most this many at once; the oldest goes when another opens. */
export const MAX_CHAT_WINDOWS = 3;

/** Below this width there's no room to dock a window: open the Chat page. */
export const CHAT_WINDOWS_MIN_WIDTH = 768;

const STORAGE_KEY = "chat-windows";

const load = (): ChatWindowState[] => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(
        (w): w is ChatWindowState =>
          !!w && Number.isInteger(w.conversationId) && w.conversationId > 0,
      )
      .map((w) => ({ conversationId: w.conversationId, minimized: !!w.minimized }))
      .slice(0, MAX_CHAT_WINDOWS);
  } catch {
    return [];
  }
};

let windows: ChatWindowState[] = typeof window === "undefined" ? [] : load();
const listeners = new Set<() => void>();

const set = (next: ChatWindowState[]) => {
  windows = next;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Private mode / blocked storage: windows just don't survive a reload.
  }
  listeners.forEach((l) => l());
};

/** The nearest edge first (index 0 sits at the corner). */
export const openChatWindow = (conversationId: number) => {
  const rest = windows.filter((w) => w.conversationId !== conversationId);
  set([{ conversationId, minimized: false }, ...rest].slice(0, MAX_CHAT_WINDOWS));
};

export const closeChatWindow = (conversationId: number) =>
  set(windows.filter((w) => w.conversationId !== conversationId));

export const toggleChatWindow = (conversationId: number) =>
  set(
    windows.map((w) =>
      w.conversationId === conversationId ? { ...w, minimized: !w.minimized } : w,
    ),
  );

export const closeAllChatWindows = () => set([]);

const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};

export const useChatWindows = () => useSyncExternalStore(subscribe, () => windows, () => windows);

/** Room to dock a window here (not a phone-sized screen). */
export const canDockChatWindows = () =>
  typeof window !== "undefined" &&
  window.matchMedia(`(min-width: ${CHAT_WINDOWS_MIN_WIDTH}px)`).matches;

/**
 * Opens a chat where it fits: a floating window, or the Chat page itself on
 * a small screen or when already there.
 */
export const openChat = (
  conversationId: number,
  opts: { onChatPage: boolean; goToChat: () => void },
) => {
  if (opts.onChatPage || !canDockChatWindows()) opts.goToChat();
  else openChatWindow(conversationId);
};

/** Test-only: start from a known state. */
export const __resetChatWindows = (next: ChatWindowState[] = []) => {
  windows = next;
  listeners.forEach((l) => l());
};
